import { normalizeCountryCode } from "@/modules/contacts/contactFormUtils";
import type { PropertyDetail } from "@/types/entities";
import type { LeaseDetails, SellingDetails } from "@/types/property";

export const PROPERTY_MARKET_OPTIONS = [
  { value: "For Sale", label: "Sale" },
  { value: "To Let", label: "Rent" },
] as const;

const LEASEHOLD_TENURE_VALUES = new Set([
  "LEASEHOLD",
  "LEASEHOLD_WITH_SHARE_OF_FREEHOLD",
  "FLYING_FREEHOLD",
  "SHARE_OF_FREEHOLD",
]);

/** Normalise CRM / UI tenure strings to a comparable enum-style key. */
export function normalizeTenureValue(tenure: string): string {
  return tenure.trim().toUpperCase().replace(/[\s-]+/g, "_");
}

export function isLeaseholdTenure(tenure: string | null | undefined): boolean {
  if (!tenure?.trim()) return false;
  return LEASEHOLD_TENURE_VALUES.has(normalizeTenureValue(tenure));
}

export interface PropertyListingReadOnly {
  bedrooms?: number | null;
  bathrooms?: number | null;
  price?: number | null;
  rentalFrequency?: string | null;
  currency?: string | null;
}

function hasListingReadOnlyValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "number") return !Number.isNaN(value);
  if (typeof value === "string") return value.trim().length > 0;
  return false;
}

/** Extract listing fields from GET /properties/{id} for read-only display on update. */
export function propertyListingReadOnlyFromDetail(
  data: PropertyDetail,
): PropertyListingReadOnly | null {
  const raw = data as PropertyDetail & Record<string, unknown>;
  const details: PropertyListingReadOnly = {
    bedrooms: typeof data.bedrooms === "number" ? data.bedrooms : null,
    bathrooms: typeof data.bathrooms === "number" ? data.bathrooms : null,
    price: typeof data.price === "number" ? data.price : null,
    currency: typeof data.currency === "string" ? data.currency.trim() || null : null,
    rentalFrequency:
      typeof raw.rentalFrequency === "string" ? raw.rentalFrequency.trim() || null : null,
  };

  const hasAny = Object.values(details).some(hasListingReadOnlyValue);
  return hasAny ? details : null;
}

export interface PropertyFormValues {
  market: string;
  branchId: string;
  negotiatorId: string;
  addressSubDwelling: string;
  addressNameNumber: string;
  street: string;
  locality: string;
  town: string;
  county: string;
  postcode: string;
  countryCode: string;
  uprn: string;
  tenureType: string;
  leaseYearsRemaining: string;
  leaseEndDate: string;
}

export function emptyPropertyFormValues(): PropertyFormValues {
  return {
    market: "",
    branchId: "",
    negotiatorId: "",
    addressSubDwelling: "",
    addressNameNumber: "",
    street: "",
    locality: "",
    town: "",
    county: "",
    postcode: "",
    countryCode: "",
    uprn: "",
    tenureType: "",
    leaseYearsRemaining: "",
    leaseEndDate: "",
  };
}

function strField(value: unknown): string {
  return typeof value === "string" ? value : value != null ? String(value) : "";
}

function intField(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isNaN(n) ? "" : String(n);
}

function dateField(value: unknown): string {
  const s = strField(value);
  if (!s) return "";
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function trimOrNull(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed || null;
}

function readAddressFromDetail(raw: PropertyDetail & Record<string, unknown>) {
  const nested = raw.addressDetails as Record<string, unknown> | null | undefined;
  const sub =
    strField(raw.addressSubDwelling) ||
    strField(nested?.addressSubDwelling);
  const nameNumber =
    strField(raw.addressNameNumber) ||
    strField(nested?.addressNameNumber) ||
    strField(raw.buildingNumber) ||
    strField(nested?.buildingNumber) ||
    strField(raw.buildingName) ||
    strField(nested?.buildingName);
  const street =
    strField(raw.street) || strField(raw.addressLine1) || strField(nested?.addressLine1);
  // Top-level `locality` on Property is string[] — address lines use addressLine2–4.
  const locality =
    (typeof raw.locality === "string" ? raw.locality.trim() : "") ||
    strField(raw.addressLine2) ||
    strField(nested?.addressLine2);
  const town =
    strField(raw.town) || strField(raw.addressLine3) || strField(nested?.addressLine3);
  const county =
    strField(raw.county) || strField(raw.addressLine4) || strField(nested?.addressLine4);
  const postcode = strField(raw.postcode) || strField(nested?.postcode);
  const countryCode =
    strField(raw.countryCode).toUpperCase().slice(0, 2) ||
    strField(raw.countryId).toUpperCase().slice(0, 2) ||
    strField(nested?.countryId).toUpperCase().slice(0, 2) ||
    "GB";
  const uprn = strField(raw.uprn);

  return { sub, nameNumber, street, locality, town, county, postcode, countryCode, uprn };
}

/**
 * Map UI address fields to Property wire names.
 * Sub Dwelling → addressSubDwelling; street → addressLine1.
 * Do not send top-level `locality` (string[] on Property).
 */
function propertyAddressFieldsForApi(values: PropertyFormValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const addressSubDwelling = trimOrNull(values.addressSubDwelling);
  const addressLine1 = trimOrNull(values.street);
  const addressLine2 = trimOrNull(values.locality);
  const addressLine3 = trimOrNull(values.town);
  const addressLine4 = trimOrNull(values.county);
  const countryId = normalizeCountryCode(values.countryCode);
  const uprn = trimOrNull(values.uprn);

  if (addressSubDwelling) out.addressSubDwelling = addressSubDwelling;
  if (addressLine1) out.addressLine1 = addressLine1;
  if (addressLine2) out.addressLine2 = addressLine2;
  if (addressLine3) out.addressLine3 = addressLine3;
  if (addressLine4) out.addressLine4 = addressLine4;
  if (countryId) out.countryId = countryId;
  if (uprn) out.uprn = uprn;

  return out;
}

function firstIdFromField(value: unknown): string {
  if (Array.isArray(value)) {
    for (const item of value) {
      const id = strField(item);
      if (id) return id;
    }
    return "";
  }
  return strField(value);
}

type UserRef = { id: string; externalId?: string };

/** Property negotiator id from detail — Atlas user id or CRM external user id. */
export function propertyNegotiatorUserIdCandidate(data: PropertyDetail): string {
  return strField(data.userId) || strField(data.externalUserId);
}

/** Match property userId (Atlas id or CRM external id) to Atlas user id for the dropdown. */
export function resolvePropertyNegotiatorAtlasId(
  userId: string | null | undefined,
  negotiators: UserRef[],
): string {
  const candidate = strField(userId);
  if (!candidate) return "";
  if (negotiators.some((u) => u.id === candidate)) return candidate;
  const byExternalId = negotiators.find((u) => u.externalId?.trim() === candidate);
  return byExternalId?.id ?? candidate;
}

export function propertyDetailToFormValues(
  data: PropertyDetail,
  negotiators: UserRef[] = [],
): PropertyFormValues {
  const raw = data as PropertyDetail & Record<string, unknown>;
  const selling = raw.sellingDetails as SellingDetails | undefined;
  const lease = raw.leaseDetails as LeaseDetails | undefined;
  const addr = readAddressFromDetail(raw);

  const tenureType =
    strField(raw.leaseTenureType) ||
    strField(lease?.leaseTenureType) ||
    strField(selling?.tenureType) ||
    strField(raw.tenure) ||
    (lease?.isLeasehold ? "LEASEHOLD" : "");

  return {
    market: strField(data.market),
    branchId:
      firstIdFromField(raw.branchId) ||
      strField(selling?.agencyId) ||
      strField(raw.agencyId),
    negotiatorId: resolvePropertyNegotiatorAtlasId(
      propertyNegotiatorUserIdCandidate(data),
      negotiators,
    ),
    addressSubDwelling: addr.sub,
    addressNameNumber: addr.nameNumber,
    street: addr.street,
    locality: addr.locality,
    town: addr.town,
    county: addr.county,
    postcode: addr.postcode,
    countryCode: addr.countryCode,
    uprn: addr.uprn,
    tenureType,
    leaseYearsRemaining: intField(
      raw.leaseYearsRemaining ??
        lease?.leaseYearsRemaining ??
        raw.leaseRemainingYears,
    ),
    leaseEndDate: dateField(raw.leaseEndDate ?? lease?.leaseEndDate ?? selling?.tenureExpiry),
  };
}

/** API fields that must not be changed via property update (PATCH). */
export const PROPERTY_UPDATE_LOCKED_API_FIELDS = [
  "propertyType",
  "propertySubTypeId",
  "category",
  "recordType",
  "priceQualifier",
  "currency",
  "rentalFrequency",
  "tenure",
  "tenureId",
  "status",
  "inventoryStatus",
  "archived",
  "isAtActiveStatus",
  "managementType",
  "maintenanceType",
  "instructionDate",
  "negotiator",
  "negotiatorName",
  "owners",
  "address",
  "branchId",
  "createdDate",
  "modifiedDate",
  "leaseRemainingYears",
] as const;

export function validatePropertyForm(
  values: PropertyFormValues,
  ownerContactIds: string[],
  options?: { isCreate?: boolean },
): { success: true } | { success: false; message: string } {
  const isCreate = options?.isCreate ?? true;

  if (!values.market?.trim()) return { success: false, message: "Market is required" };

  if (isCreate) {
    if (!values.branchId?.trim()) return { success: false, message: "Branch is required" };
    if (!values.negotiatorId?.trim()) return { success: false, message: "Negotiator is required" };
    if (!values.addressNameNumber?.trim()) {
      return { success: false, message: "Name/Number is required" };
    }
    if (!values.postcode?.trim()) return { success: false, message: "Postcode is required" };

    const country = normalizeCountryCode(values.countryCode);
    if (values.countryCode?.trim() && country.length !== 2) {
      return { success: false, message: "Country code must be 2 letters (e.g. GB)" };
    }

    const owners = ownerContactIds.map((id) => id.trim()).filter(Boolean);
    if (owners.length === 0) {
      return { success: false, message: "Owner is required" };
    }

    if (isLeaseholdTenure(values.tenureType)) {
      const years = values.leaseYearsRemaining.trim();
      const end = values.leaseEndDate.trim();
      if (!years && !end) {
        return {
          success: false,
          message: "Provide Remaining Term (Years) or Lease End Date for leasehold tenure",
        };
      }
      if (years) {
        const n = Number.parseInt(years, 10);
        if (Number.isNaN(n) || n < 0) {
          return { success: false, message: "Enter a valid remaining term in years" };
        }
      }
      if (end) {
        const endDate = new Date(end);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (endDate < today) {
          return { success: false, message: "Lease End Date must not be in the past" };
        }
      }
    }
  }

  return { success: true };
}

export interface PropertyOwnerPayloadRef {
  externalContactId: string;
  forename: string;
  surname: string;
}

export interface BuildPropertyPayloadContext {
  legalEntityId: string;
  sourceCrm: string;
  branchId: string;
  externalBranchId: string;
  /** Atlas user UUID for the selected negotiator. */
  userId: string;
  /** CRM external id for the selected negotiator. */
  externalUserId: string;
  owners: PropertyOwnerPayloadRef[];
}

/** Map form market values to API market strings (e.g. To Let → Rent). */
export function mapPropertyMarketForApi(market: string): string {
  const m = market.trim();
  if (m === "To Let") return "Rent";
  if (m === "For Sale") return "Sale";
  return m;
}

function nameNumberFieldsForApi(value: string): Record<string, string> {
  const t = value.trim();
  if (!t) {
    throw new Error("Name / Number is required");
  }
  if (/^\d+[a-zA-Z]?$/.test(t)) {
    return { buildingNumber: t };
  }
  return { buildingName: t };
}

type BranchRef = { id: string; sourceCrm?: string; externalId?: string };
type ContactRef = {
  id: string;
  externalId?: string;
  forename?: string | null;
  surname?: string | null;
  name?: string | null;
};

function ownerNameFromContact(contact: ContactRef): { forename: string; surname: string } {
  const forename = contact.forename?.trim() ?? "";
  const surname = contact.surname?.trim() ?? "";
  if (forename || surname) return { forename, surname };

  const name = contact.name?.trim() ?? "";
  if (!name) return { forename: "", surname: "" };
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { forename: parts[0], surname: "" };
  return { forename: parts[0], surname: parts.slice(1).join(" ") };
}

export function resolvePropertyApiContext(input: {
  values: PropertyFormValues;
  legalEntityId: string;
  ownerContactIds: string[];
  branches: BranchRef[];
  negotiators: UserRef[];
  contacts: ContactRef[];
}): { success: true; ctx: BuildPropertyPayloadContext } | { success: false; message: string } {
  const branch = input.branches.find((b) => b.id === input.values.branchId.trim());
  if (!branch) {
    return { success: false, message: "Selected branch could not be resolved" };
  }
  const sourceCrm = branch.sourceCrm?.trim();
  const externalBranchId = branch.externalId?.trim();
  if (!sourceCrm) {
    return { success: false, message: "Branch source CRM is missing — reload branches and try again" };
  }
  if (!externalBranchId) {
    return { success: false, message: "Branch external ID is missing — reload branches and try again" };
  }

  const negotiatorId = input.values.negotiatorId.trim();
  const negotiator = input.negotiators.find(
    (u) => u.id === negotiatorId || u.externalId?.trim() === negotiatorId,
  );
  if (!negotiator) {
    return { success: false, message: "Selected negotiator could not be resolved — select a negotiator again" };
  }
  const negotiatorAtlasId = negotiator.id.trim();
  if (!negotiatorAtlasId) {
    return { success: false, message: "Negotiator user ID is missing — select a negotiator again" };
  }
  const negotiatorExternalId = negotiator.externalId?.trim();
  if (!negotiatorExternalId) {
    return { success: false, message: "Negotiator external ID is missing — select a negotiator again" };
  }

  const owners: PropertyOwnerPayloadRef[] = [];
  for (const contactId of input.ownerContactIds.map((id) => id.trim()).filter(Boolean)) {
    const contact = input.contacts.find((c) => c.id === contactId);
    if (!contact) {
      return {
        success: false,
        message: "Selected owner could not be resolved — re-select the owner from the list",
      };
    }
    const externalContactId = contact.externalId?.trim();
    if (!externalContactId) {
      return {
        success: false,
        message: "Owner external contact ID is missing — re-select the owner from the list",
      };
    }
    const { forename, surname } = ownerNameFromContact(contact);
    owners.push({ externalContactId, forename, surname });
  }

  return {
    success: true,
    ctx: {
      legalEntityId: input.legalEntityId,
      sourceCrm,
      branchId: branch.id,
      externalBranchId,
      userId: negotiatorAtlasId,
      externalUserId: negotiatorExternalId,
      owners,
    },
  };
}

function propertyLeaseFieldsForApi(values: PropertyFormValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const leaseTenureType = trimOrNull(values.tenureType);
  if (leaseTenureType) out.leaseTenureType = leaseTenureType;

  if (!isLeaseholdTenure(values.tenureType)) return out;

  const years = values.leaseYearsRemaining.trim();
  if (years) {
    const n = Number.parseInt(years, 10);
    if (!Number.isNaN(n)) out.leaseYearsRemaining = n;
  }

  const leaseEndDate = trimOrNull(values.leaseEndDate);
  if (leaseEndDate) out.leaseEndDate = leaseEndDate;

  return out;
}

/** Minimal property create body aligned with POST /properties. */
export function buildPropertyCreatePayload(
  values: PropertyFormValues,
  ctx: BuildPropertyPayloadContext,
): Record<string, unknown> {
  return {
    legalEntityId: ctx.legalEntityId,
    sourceCrm: ctx.sourceCrm,
    externalBranchId: [ctx.externalBranchId],
    branchId: [ctx.branchId],
    market: mapPropertyMarketForApi(values.market),
    ...nameNumberFieldsForApi(values.addressNameNumber),
    postcode: values.postcode.trim(),
    ...propertyAddressFieldsForApi(values),
    ...propertyLeaseFieldsForApi(values),
    owners: ctx.owners.map((owner) => ({
      links: null,
      externalContactId: owner.externalContactId,
      forename: owner.forename,
      surname: owner.surname,
    })),
    userId: ctx.userId,
    externalUserId: ctx.externalUserId,
  };
}

/** Only fields not listed in PROPERTY_UPDATE_LOCKED_API_FIELDS may be sent on update. */
export function buildPropertyUpdatePatch(values: PropertyFormValues): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  const market = values.market?.trim();
  if (market) {
    patch.market = mapPropertyMarketForApi(market);
  }

  const leaseEndDate = trimOrNull(values.leaseEndDate);
  if (leaseEndDate) {
    patch.leaseEndDate = leaseEndDate;
  }

  return patch;
}

export { formatPropertyOwnerLabel } from "@/lib/propertyDisplay";
